import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
} from '@mui/material';
import { BRAND_COLORS } from '../styles/theme';
import { useAuthV2 } from '../contexts/AuthContextV2';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useTranslation, LanguageSwitcher } from '../i18n';
import XERLogo from './XERLogo';

const AuthForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const { signIn } = useAuthV2();
  const { showError, showSuccess } = useErrorHandler();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.email) {
      newErrors.push(t('auth.emailRequired'));
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.push(t('auth.emailInvalid'));
    }

    if (!formData.password) {
      newErrors.push(t('auth.passwordRequired'));
    } else if (formData.password.length < 6) {
      newErrors.push(t('auth.passwordMinLength'));
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSignIn = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    const { error } = await signIn(formData.email, formData.password);

    if (error) {
      // PENDING_APPROVAL redirects to /pending-approval, so we only handle other errors here
      if (error.message !== 'PENDING_APPROVAL') {
        setErrors([error.message]);
        showError(error, t('errors.generic'));
      }
    } else {
      showSuccess(t('auth.loginSuccessful'));
      // Clear form data after successful login to prevent password exposure
      setFormData({ email: '', password: '' });
    }
    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/login-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: 2,
        position: 'relative',
      }}
    >
      {/* Language Switcher - top right */}
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <LanguageSwitcher variant="compact" />
      </Box>

      <Paper
        elevation={3}
        sx={{
          padding: 4,
          maxWidth: 400,
          width: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          backdropFilter: 'blur(8px)',
        }}
      >
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <XERLogo width={120} height={50} />
          <Typography
            variant="h5"
            sx={{
              mt: 2,
              color: BRAND_COLORS.forestGreen.main,
              fontWeight: 600,
            }}
          >
            {t('common.appName')}
          </Typography>
        </Box>

        {/* Error Messages */}
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        {/* Sign In Form */}
        <Box>
          <form onSubmit={handleSignIn}>
            <TextField
              fullWidth
              label={t('auth.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              margin="normal"
              disabled={loading}
              autoComplete="email"
            />
            <TextField
              fullWidth
              label={t('auth.password')}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              disabled={loading}
              autoComplete="current-password"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                backgroundColor: BRAND_COLORS.forestGreen.main,
                '&:hover': {
                  backgroundColor: '#002B20',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : t('auth.signIn')}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate('/forgot-password')}
                disabled={loading}
                sx={{ color: BRAND_COLORS.forestGreen.main }}
              >
                {t('auth.forgotPassword')}
              </Link>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              {t('auth.invitationOnly')}
            </Typography>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default AuthForm;
